<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'

import UiCta from '@/components/common/UiCta.vue'
import UiIcon from '@/components/common/UiIcon.vue'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { useHeaderNavContent } from '@/composables/useHeaderNavContent'
import { localizedPath } from '@/router/i18nPath'
import { localizedPagePath } from '@/router/pageSlugs'
import { useUIStore } from '@/stores/ui.store'

type MobileNavSectionKey = 'gym' | 'trainer'

type MobileNavSection = {
  key: MobileNavSectionKey
  label: string
  overviewTo: string
  items: Array<{
    id: number
    title: string
    to: string
    description?: string
  }>
}

defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()
const { t } = useI18n()
const ui = useUIStore()
const { isOffcanvasOpen } = storeToRefs(ui)
const { closeOffcanvas } = ui

const panelRef = ref<HTMLElement | null>(null)
const isTeleportReady = ref(false)
const isPanelContentVisible = ref(false)
const expandedSections = ref<Record<MobileNavSectionKey, boolean>>({
  gym: false,
  trainer: false,
})
let panelContentTimer: number | null = null

const { gymItems, trainerItems, formatGymDetails, formatTrainerDetails, getTrainerName } = useHeaderNavContent()

const offcanvasSections = computed<MobileNavSection[]>(() => [
  {
    key: 'gym',
    label: t('menu.gym'),
    overviewTo: localizedPagePath('gymOverview'),
    items: gymItems.value.map((gym) => ({
      id: gym.id,
      title: gym.title,
      to: localizedPath('gym', { slug: gym.slug }),
      description: formatGymDetails(gym),
    })),
  },
  {
    key: 'trainer',
    label: t('menu.trainer'),
    overviewTo: localizedPagePath('trainerOverview'),
    items: trainerItems.value.map((trainer) => ({
      id: trainer.id,
      title: getTrainerName(trainer),
      to: localizedPath('trainer', { slug: trainer.slug }),
      description: formatTrainerDetails(trainer),
    })),
  },
])

const navAttrs = computed(() => ({
  id: typeof attrs.id === 'string' ? attrs.id : 'primary-menu',
  role: typeof attrs.role === 'string' ? attrs.role : 'navigation',
  'aria-label': typeof attrs['aria-label'] === 'string'
    ? attrs['aria-label']
    : t('menu.mobileMenu'),
}))

const { activate, deactivate } = useFocusTrap(panelRef, {
  escapeDeactivates: false,
  initialFocus: '#mobile-menu-close-button',
  restoreFocus: true,
})

function isSectionOpen(key: MobileNavSectionKey) {
  return expandedSections.value[key]
}

function toggleSection(key: MobileNavSectionKey) {
  const nextOpenState = !expandedSections.value[key]

  expandedSections.value.gym = false
  expandedSections.value.trainer = false
  expandedSections.value[key] = nextOpenState
}

function getEntryClasses() {
  return isPanelContentVisible.value
    ? 'offcanvas-entry offcanvas-entry--visible'
    : 'offcanvas-entry offcanvas-entry--hidden'
}

function getEntryStyle(index: number) {
  if (!isPanelContentVisible.value) return undefined

  return {
    transitionDelay: `${index * 75}ms`,
  }
}

function closeAndResetOffcanvas() {
  expandedSections.value.gym = false
  expandedSections.value.trainer = false
  closeOffcanvas()
}

function clearPanelContentTimer() {
  if (panelContentTimer === null || typeof window === 'undefined') return

  window.clearTimeout(panelContentTimer)
  panelContentTimer = null
}

function handlePanelBeforeEnter() {
  isPanelContentVisible.value = false
  clearPanelContentTimer()

  if (typeof window === 'undefined') return

  panelContentTimer = window.setTimeout(() => {
    isPanelContentVisible.value = true
    panelContentTimer = null
  }, 180)
}

function handlePanelAfterEnter() {
  clearPanelContentTimer()
  isPanelContentVisible.value = true
}

function handlePanelBeforeLeave() {
  clearPanelContentTimer()
  isPanelContentVisible.value = false
}

function handleDesktopBreakpoint(event: MediaQueryListEvent | MediaQueryList) {
  if (event.matches) {
    closeAndResetOffcanvas()
  }
}

let desktopMediaQuery: MediaQueryList | null = null

onMounted(() => {
  isTeleportReady.value = true

  if (typeof window === 'undefined') return

  desktopMediaQuery = window.matchMedia('(min-width: 1024px)')
  handleDesktopBreakpoint(desktopMediaQuery)
  desktopMediaQuery.addEventListener('change', handleDesktopBreakpoint)
})

onBeforeUnmount(() => {
  desktopMediaQuery?.removeEventListener('change', handleDesktopBreakpoint)
  clearPanelContentTimer()
  deactivate()
})

watch(isOffcanvasOpen, async (open) => {
  if (open) {
    await nextTick()
    activate()
    return
  }

  deactivate()
  expandedSections.value.gym = false
  expandedSections.value.trainer = false
})
</script>

<template>
  <Teleport v-if="isTeleportReady" to="body">
    <Transition
      enter-active-class="transition-opacity duration-350 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOffcanvasOpen"
        aria-hidden="true"
        class="fixed inset-0 z-50 bg-gray-900/30 lg:hidden"
        @click="closeAndResetOffcanvas"
      />
    </Transition>

    <Transition
      name="offcanvas-panel"
      @before-enter="handlePanelBeforeEnter"
      @after-enter="handlePanelAfterEnter"
      @before-leave="handlePanelBeforeLeave"
    >
      <aside
        v-if="isOffcanvasOpen"
        ref="panelRef"
        class="fixed inset-y-0 right-0 z-[60] flex w-full max-w-md lg:hidden"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-heading"
        @click.stop
      >
        <div class="flex h-full w-full flex-col overflow-y-auto bg-background px-5 pb-6 pt-5 shadow-2xl">
          <div class="flex items-center justify-between gap-4 border-b border-border pb-5">
            <div>
              <h2 id="mobile-menu-heading" class="text-2xl font-semibold text-foreground">
                {{ navAttrs['aria-label'] }}
              </h2>
            </div>

            <button
              id="mobile-menu-close-button"
              type="button"
              :aria-label="t('menu.closeMenu')"
              class="btn-ui btn-ui--ghost btn-ui--icon rounded-full text-muted-foreground"
              @click="closeAndResetOffcanvas"
            >
              <UiIcon
                name="X"
                class="size-5"
              />
            </button>
          </div>

          <nav
            v-bind="navAttrs"
            class="mt-6 flex flex-col gap-3"
          >
            <section
              v-for="section in offcanvasSections"
              :key="section.key"
              class="rounded-2xl border border-gray-200 px-4 py-4 dark:border-base-300"
              :class="getEntryClasses()"
              :style="getEntryStyle(offcanvasSections.findIndex(({ key }) => key === section.key) + 1)"
            >
              <div class="flex items-center gap-3">
                <RouterLink
                  :to="section.overviewTo"
                  class="min-w-0 flex-1 text-lg font-semibold text-black transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 dark:text-inherit"
                  @click="closeAndResetOffcanvas"
                >
                  {{ section.label }}
                </RouterLink>

                <button
                  type="button"
                  class="inline-flex shrink-0 items-center justify-center rounded-md p-2 text-black transition-colors hover:bg-brand-sand/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 dark:text-inherit dark:hover:bg-base-200"
                  :aria-controls="`mobile-menu-${section.key}`"
                  :aria-expanded="isSectionOpen(section.key)"
                  @click="toggleSection(section.key)"
                >
                  <span class="sr-only">{{ section.label }}</span>
                  <UiIcon
                    name="ChevronDown"
                    :size="20"
                    :class="isSectionOpen(section.key)
                      ? 'transition-transform duration-200 rotate-180'
                      : 'transition-transform duration-200'"
                  />
                </button>
              </div>

              <Transition
                enter-active-class="transition-all duration-250 ease-out overflow-hidden"
                enter-from-class="max-h-0 opacity-0 -translate-y-2"
                enter-to-class="max-h-[80vh] opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in overflow-hidden"
                leave-from-class="max-h-[80vh] opacity-100 translate-y-0"
                leave-to-class="max-h-0 opacity-0 -translate-y-2"
              >
                <div
                  v-if="isSectionOpen(section.key)"
                  :id="`mobile-menu-${section.key}`"
                  class="overflow-hidden"
                >
                  <ul class="mt-4 space-y-2 border-t border-gray-200 pt-4 dark:border-base-300">
                    <li
                      v-for="item in section.items"
                      :key="item.id"
                    >
                      <RouterLink
                        :to="item.to"
                        class="block rounded-xl bg-gray-50 px-4 py-3 transition-colors hover:bg-brand-sand/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 dark:bg-base-200 dark:hover:bg-base-300"
                        @click="closeAndResetOffcanvas"
                      >
                        <span class="block font-medium text-black dark:text-inherit">{{ item.title }}</span>
                        <span
                          v-if="item.description"
                          class="mt-1 block text-sm leading-relaxed text-gray-600 dark:text-inherit"
                        >
                          {{ item.description }}
                        </span>
                      </RouterLink>
                    </li>
                  </ul>
                </div>
              </Transition>
            </section>

            <RouterLink
              :to="localizedPagePath('memberships')"
              class="rounded-2xl border border-gray-200 px-4 py-4 text-lg font-semibold text-black transition-colors hover:border-brand-green hover:bg-brand-sand/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 dark:border-base-300 dark:text-inherit dark:hover:bg-base-200"
              :class="getEntryClasses()"
              :style="getEntryStyle(offcanvasSections.length + 1)"
              @click="closeAndResetOffcanvas"
            >
              {{ t('menu.memberships') }}
            </RouterLink>

            <RouterLink
              :to="localizedPagePath('customerService')"
              class="rounded-2xl border border-gray-200 px-4 py-4 text-lg font-semibold text-black transition-colors hover:border-brand-green hover:bg-brand-sand/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 dark:border-base-300 dark:text-inherit dark:hover:bg-base-200"
              :class="getEntryClasses()"
              :style="getEntryStyle(offcanvasSections.length + 2)"
              @click="closeAndResetOffcanvas"
            >
              {{ t('menu.customerService') }}
            </RouterLink>
          </nav>

          <div
            class="mt-6 border-t border-gray-200 pt-5 dark:border-base-300"
            :class="getEntryClasses()"
            :style="getEntryStyle(offcanvasSections.length + 3)"
          >
            <div class="flex flex-col gap-3">
              <UiCta
                class="w-full justify-center"
                :options="{
                  type: 'internal',
                  text: t('cta.memberships'),
                  icon: 'Tickets',
                  color: 'primary',
                  to: localizedPagePath('memberships')
                }"
                @click.capture="closeAndResetOffcanvas"
              />

              <UiCta
                class="w-full justify-center"
                :options="{
                  type: 'external',
                  text: t('cta.login'),
                  icon: 'UserCog',
                  color: 'primary',
                  to: 'https://my.fitnessroom.no/pages/login',
                  utm: {
                    utm_source: 'mobile_navigation',
                    utm_medium: 'webpage',
                    utm_campaign: 'member_login'
                  }
                }"
                @click.capture="closeAndResetOffcanvas"
              />
            </div>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>
